import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Truck, Award } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useApp } from '../context/AppContext';

export default function Home() {
  const { state } = useApp();
  const featuredProducts = state.products.slice(0, 8);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
              Fragrances for Men - Signature Scents by <span className="text-red-500">AKIDO™</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              Discover premium fragrances crafted for the modern gentleman. Each scent tells a unique story.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 px-4">
              <Link
                to="/products"
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Shop Now</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">Featured Collection</h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              Our most popular and exclusive fragrances, loved by thousands
            </p>
          </div>

          {/* Grid Layout: 2 columns on mobile, 4 on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/products"
              className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105"
            >
              <span>View All Products</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">Why Choose AKIDO™?</h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              We're committed to providing you with the finest fragrance experience
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800 hover:border-red-500/40 transition-all duration-300 hover:scale-105 text-center">
              <div className="bg-red-500 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Premium Quality</h3>
              <p className="text-gray-400 text-sm sm:text-base">Authentic fragrances from world-renowned perfumers</p>
            </div>

            <div className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800 hover:border-red-500/40 transition-all duration-300 hover:scale-105 text-center">
              <div className="bg-red-500 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Fast Delivery</h3>
              <p className="text-gray-400 text-sm sm:text-base">Free shipping across India with express delivery options</p>
            </div>

            <div className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800 hover:border-red-500/40 transition-all duration-300 hover:scale-105 text-center">
              <div className="bg-red-500 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Secure Shopping</h3>
              <p className="text-gray-400 text-sm sm:text-base">100% secure payment with money-back guarantee</p>
            </div>

            <div className="bg-gray-900 rounded-3xl p-4 sm:p-6 border border-gray-800 hover:border-red-500/40 transition-all duration-300 hover:scale-105 text-center">
              <div className="bg-red-500 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <Star className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Expert Curation</h3>
              <p className="text-gray-400 text-sm sm:text-base">Handpicked fragrances by our perfume experts</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-red-500/10 to-red-600/10 border border-red-500/30 rounded-3xl p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Find Your Signature Scent
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who have discovered their perfect fragrance with AKIDO™
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/products"
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105"
              >
                Start Shopping
              </Link>
              <Link
                to="/signin"
                className="w-full sm:w-auto border border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-all duration-300"
              >
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}